//src/pages/NotificationsPage.tsx
import { useEffect, useMemo, useState } from "react";
import {
  BellRing,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  ShieldAlert,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/components/shared/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNotifications } from "@/hooks/useNotifications";
import type { NotificationItem } from "@/types/notifications";

function formatDate(value: string) {
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatRelativeDate(value: string) {
  const date = new Date(value);
  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60000);
  const rtf = new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" });

  if (Math.abs(diffMinutes) < 60) return rtf.format(diffMinutes, "minute");
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) return rtf.format(diffHours, "hour");
  const diffDays = Math.round(diffHours / 24);
  return rtf.format(diffDays, "day");
}

function getNotificationIcon(item: NotificationItem) {
  if (item.type === "campaign_low_engagement" || item.type === "campaign_closing_soon_pending_response") {
    return ShieldAlert;
  }
  if (item.type === "action_plan_item_assigned") {
    return ClipboardCheck;
  }
  return BellRing;
}

function getSeverityBadge(item: NotificationItem) {
  if (item.severity === "critical") {
    return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Crítica</Badge>;
  }
  if (item.severity === "warning") {
    return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Atenção</Badge>;
  }
  return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Informativa</Badge>;
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"all" | "unread">("all");
  const [page, setPage] = useState(1);

  const { data, unreadCount, loading, actionLoading, load, markOneAsRead, markAllAsRead } = useNotifications({
    page,
    per_page: 10,
    is_read: tab === "unread" ? false : undefined,
  });

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const handleRefresh = () => {
      if (document.visibilityState === "visible") {
        void load();
      }
    };

    window.addEventListener("focus", handleRefresh);
    document.addEventListener("visibilitychange", handleRefresh);

    return () => {
      window.removeEventListener("focus", handleRefresh);
      document.removeEventListener("visibilitychange", handleRefresh);
    };
  }, [load]);

  const titleDescription = useMemo(() => {
    if (unreadCount > 0) return `${unreadCount} notificação(ões) ainda aguardam sua leitura.`;
    return "Acompanhe seus alertas gerais e as pendências direcionadas a você.";
  }, [unreadCount]);

  const handleMarkAll = async () => {
    try {
      await markAllAsRead();
      toast.success("Todas as notificações foram marcadas como lidas.");

      if (tab === "unread") {
        setPage(1);
      }

      await load();
    } catch (error) {
      console.error(error);
      toast.error("Não foi possível marcar tudo como lido.");
    }
  };

  const handleNotificationClick = async (item: NotificationItem) => {
    try {
      if (!item.is_read) {
        await markOneAsRead(item.id);
        await load();
      }

      const targetRoute = item.route || "/notificacoes";

      if (item.type === "campaign_available_to_answer") {
        window.open(targetRoute, "_blank", "noopener,noreferrer");
        return;
      }

      navigate(targetRoute);
    } catch (error) {
      console.error(error);
      toast.error("Não foi possível abrir a notificação.");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Notificações" description={titleDescription}>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl"
            disabled={actionLoading || unreadCount === 0}
            onClick={() => void handleMarkAll()}
          >
            <Check className="mr-2 h-4 w-4" />
            Marcar todas como lidas
          </Button>
        </div>
      </PageHeader>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <Tabs
          value={tab}
          onValueChange={(value) => {
            setTab(value as "all" | "unread");
            setPage(1);
          }}
        >
          <TabsList className="grid w-full grid-cols-2 rounded-xl lg:w-[280px]">
            <TabsTrigger value="all" className="rounded-lg">
              Todas
            </TabsTrigger>
            <TabsTrigger value="unread" className="rounded-lg">
              Não lidas
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="secondary" className="rounded-full px-3 py-1">
            {data.total} no total
          </Badge>
          <Badge variant="secondary" className="rounded-full px-3 py-1">
            {unreadCount} não lidas
          </Badge>
        </div>
      </div>

      <div className="space-y-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Card key={index} className="rounded-2xl border shadow-sm">
              <CardContent className="p-5">
                <div className="space-y-3">
                  <div className="h-4 w-40 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-full animate-pulse rounded bg-muted" />
                  <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
                </div>
              </CardContent>
            </Card>
          ))
        ) : data.items.length > 0 ? (
          data.items.map((item) => {
            const Icon = getNotificationIcon(item);

            return (
              <Card
                key={item.id}
                className={`cursor-pointer rounded-2xl border transition-all hover:-translate-y-0.5 hover:shadow-md ${!item.is_read ? "border-primary/20 bg-primary/5" : "bg-card"}`}
                onClick={() => void handleNotificationClick(item)}
              >
                <CardContent className="flex gap-4 p-5">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-background shadow-sm">
                    <Icon className="h-5 w-5" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className={`text-sm ${!item.is_read ? "font-semibold" : "font-medium text-foreground/85"}`}>
                            {item.title}
                          </p>
                          {getSeverityBadge(item)}
                          {item.is_general ? <Badge variant="outline">Geral</Badge> : <Badge variant="outline">Para você</Badge>}
                          {!item.is_read && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                        </div>
                        <p className="text-sm leading-relaxed text-muted-foreground">{item.message}</p>
                      </div>

                      <div className="shrink-0 text-right text-xs text-muted-foreground">
                        <p>{formatRelativeDate(item.created_at)}</p>
                        <p>{formatDate(item.created_at)}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card className="rounded-3xl border border-dashed">
            <CardContent className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
              <BellRing className="h-12 w-12 text-muted-foreground/60" />
              <div className="space-y-1">
                <p className="text-base font-semibold">Nenhuma notificação encontrada</p>
                <p className="max-w-md text-sm text-muted-foreground">
                  {tab === "unread"
                    ? "Você não possui notificações não lidas neste momento."
                    : "Quando o sistema identificar algo importante para você ou para o seu tenant, vai aparecer aqui."}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="flex flex-col gap-3 rounded-2xl border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Página <span className="font-medium text-foreground">{data.page}</span> de{" "}
          <span className="font-medium text-foreground">{data.total_pages}</span>
        </p>

        <div className="flex gap-2">
          <Button
            variant="outline"
            className="rounded-xl"
            disabled={data.page <= 1 || loading}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Anterior
          </Button>
          <Button
            variant="outline"
            className="rounded-xl"
            disabled={data.page >= data.total_pages || loading}
            onClick={() => setPage((current) => Math.min(data.total_pages, current + 1))}
          >
            Próxima
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
